import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import BankView from './BankView';
import withPermission from '../../../utils/withPermission';
import { useAuth } from '../../../context/AuthContext';
import TransactionList from './TransactionList';
import { Card, Tabs, Tab, Button, Offcanvas, Badge, Row, Col } from 'react-bootstrap';
import { BiPencil, BiLinkExternal } from "react-icons/bi";
import { BsX, BsBank, BsArrowLeftRight, BsInfoCircle, BsGeoAlt, BsWallet2 } from "react-icons/bs";
import BankModal from './BankModal';
import Swal from 'sweetalert2';

const BankDetail = ({ showOffcanvas, handleClose, bankId, onBankUpdate }) => {
    const [bankDetails, setBankDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('transactionList');
    const { permissions, companySettings } = useAuth();
    const didFetchRef = useRef(false);
    const [selectedBank, setSelectedBank] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [countries, setCountries] = useState([]);
    const [closingBalance, setClosingBalance] = useState(null);
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [ledgerOpeningBalance, setLedgerOpeningBalance] = useState(null);
    const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [isLoadingLedger, setIsLoadingLedger] = useState(false);

    const fetchBankDetails = async () => {
        if (!bankId || isLoadingDetails) return;
        setIsLoadingDetails(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/bank-accounts/${bankId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setBankDetails(response.data);
        } catch (error) {
            console.error('Error fetching bank details:', error);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const fetchClosingBalance = async (bankName) => {
        if (!bankName || isLoadingLedger) return;
        setIsLoadingLedger(true);
        try {
            const Api = process.env.REACT_APP_WEBZASH_URL;
            const apiKey = process.env.REACT_APP_API_KEY;

            const ledgersResponse = await axios.get(`${Api}/api/ledgers/index`, {
                headers: { 'x-api-key': apiKey, 'Accept': 'application/json' },
                skipLoader: true,
            });

            const ledgerList = ledgersResponse.data?.data || [];
            const matchingLedger = ledgerList.find(item => 
                item.Ledger?.name?.toLowerCase() === bankName.toLowerCase()
            );

            if (!matchingLedger) {
                setIsLoadingLedger(false);
                return;
            }

            const ledgerId = matchingLedger.Ledger.id;

            const response = await axios.get(`${Api}/api/reports/ledgerstatement`, {
                params: { ledger_id: ledgerId },
                headers: { 'x-api-key': apiKey, 'Accept': 'application/json' },
                skipLoader: true,
            });
            if (response.data) {
                setClosingBalance(response.data.closing_balance);
                
                // Process entries for running balance calculation
                const { entries, opening_balance } = response.data;
                const entryGroups = (entries || []).reduce((acc, item) => {
                    const entryId = item.Entry.id;
                    if (!acc[entryId]) {
                        acc[entryId] = {
                            id: entryId,
                            date: item.Entry.date,
                            narration: item.Entry.narration,
                            netLedgerAmount: 0
                        };
                    }
                    if (String(item.Entryitem.ledger_id) === String(ledgerId)) {
                        const amount = parseFloat(item.Entryitem.amount || 0);
                        if (item.Entryitem.dc === 'D') {
                            acc[entryId].netLedgerAmount += amount;
                        } else {
                            acc[entryId].netLedgerAmount -= amount;
                        }
                    }
                    return acc;
                }, {});

                const sortedGroups = Object.values(entryGroups).sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    if (dateA < dateB) return -1;
                    if (dateA > dateB) return 1;
                    return a.id - b.id;
                });

                let balance = parseFloat(opening_balance?.amount || 0);
                if (opening_balance?.dc === 'C') balance = -balance;

                const processedEntries = sortedGroups.map(group => {
                    balance += group.netLedgerAmount;
                    return { ...group, runningBalance: balance };
                });

                setLedgerEntries(processedEntries);
                setLedgerOpeningBalance(opening_balance);
            }
        } catch (error) {
            console.error('Error fetching closing balance:', error);
        } finally {
            setIsLoadingLedger(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
    
            const response = await axios.get(`${backendUrl}/api/countries`, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
    
            setCountries(response.data.Data);
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    const editBank = async (id, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.put(`${backendUrl}/api/bank-accounts/${id}`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
            const updatedBank = response.data?.account;

            if (updatedBank) {
                setBankDetails(updatedBank);
                if (onBankUpdate) onBankUpdate();
                
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Bank updated successfully!',
                    showConfirmButton: false,
                    timer: 3000
                });
            }
            setShowModal(false);
            setIsTemporarilyHidden(false);
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            }
            console.error('Error updating bank:', error);
        }
    };

    useEffect(() => {
        if (showOffcanvas && bankId) {
            fetchBankDetails();
            fetchCountries();
        }
    }, [bankId, showOffcanvas]);

    useEffect(() => {
        if (bankDetails?.name) {
            fetchClosingBalance(bankDetails.name);
        }
    }, [bankDetails?.name]);

    const convertAEDToBankCurrency = (ledgerBalance, bank) => {
        if (!bank || !ledgerBalance) return null;
        const bankCurrency = (bank.base_currency?.currency || bank.currency?.currency || bank.currency || 'AED').toString().trim().toUpperCase();
        let amount = parseFloat(ledgerBalance.amount || 0);
        if (ledgerBalance.dc === 'C') amount = -amount;

        if (bankCurrency === 'AED') {
            const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return `${amount >= 0 ? 'Dr' : 'Cr'} ${formatted}`;
        }

        const aedRateObj = bank.exchange_rates?.find(r => 
            (r.currency?.currency || '').toString().trim().toUpperCase() === 'AED'
        );

        if (aedRateObj) {
            const rate = parseFloat(aedRateObj.rate || aedRateObj.exchange_rate || 0);
            if (rate > 0) amount = amount * rate;
        } else {
            const globalRate = parseFloat(companySettings?.usd_to_aed || localStorage.getItem('exchange_rate') || '3.67');
            if (globalRate > 0 && bankCurrency === 'USD') amount = amount / globalRate;
        }

        const formatted = Math.abs(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        
        return `${amount >= 0 ? 'Dr' : 'Cr'} ${formatted}`;
    };

    const handleOffcanvasClose = () => {
        setBankDetails(null);
        setClosingBalance(null);
        setLedgerEntries([]);
        setLedgerOpeningBalance(null);
        setActiveTab('transactionList');
        handleClose();
    };

    return (
        <>
            <Offcanvas
                show={showOffcanvas}
                onHide={handleOffcanvasClose}
                placement="end"
                style={{ width: '1150px' }}
                enforceFocus={false}
                className={`bank-details-offcanvas ${isTemporarilyHidden ? "offcanvas-hidden" : ""}`}
                backdrop={!isTemporarilyHidden}
            >
                <Offcanvas.Header className="border-bottom bg-white py-3">
                    <div className="d-flex align-items-center w-100">
                        <div className="d-flex align-items-center flex-grow-1">
                            <div className="bg-primary rounded p-2 me-3">
                                <BsBank className="text-white" size={24} />
                            </div>
                            <div>
                                <Offcanvas.Title className="mb-0">
                                    <h4 className="fw-bold text-dark mb-1">{bankDetails?.name || 'Bank Account'}</h4>
                                </Offcanvas.Title>
                                <div className="d-flex align-items-center gap-2">
                                    <Badge bg={bankDetails?.type === 'bank account' ? 'primary' : 'success'} className="fs-12">
                                        {bankDetails?.type || 'Account'}
                                    </Badge>
                                    <span className="text-muted fs-13">
                                        {bankDetails?.branch_name}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center gap-2"
                                onClick={() => {
                                    setIsTemporarilyHidden(true);
                                    setTimeout(() => {
                                        setSelectedBank(bankDetails);
                                        setShowModal(true);
                                    }, 200);
                                }}
                            >
                                <BiPencil size={16} />
                                Edit
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="d-flex align-items-center gap-2"
                                onClick={handleOffcanvasClose}
                            >
                                <BsX size={18} />
                                Close
                            </Button>
                        </div>
                    </div>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-0 bg-light">
                    {bankDetails ? (
                        <div className="bank-details-erp">
                            {/* Quick Stats Bar */}
                            <div className="bg-white border-bottom">
                                <div className="px-4 py-3">
                                    <Row className="g-3">
                                        <Col md={3}>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-light rounded p-2 me-3">
                                                    <BsWallet2 className="text-primary" size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-muted fs-12 fw-semibold">OPENING BALANCE</div>
                                                    <div className="fw-semibold text-dark">
                                                        {bankDetails?.currency?.currency} {bankDetails?.opening_balance || '0.00'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-light rounded p-2 me-3">
                                                    <BsArrowLeftRight className="text-success" size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-muted fs-12 fw-semibold">RUNNING BALANCE</div>
                                                    <div className="fw-semibold text-success">
                                                        {bankDetails?.currency?.currency} {convertAEDToBankCurrency(closingBalance, bankDetails) || '0.00'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-light rounded p-2 me-3">
                                                    <BsGeoAlt className="text-warning" size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-muted fs-12 fw-semibold">CURRENCY</div>
                                                    <div className="fw-semibold text-dark">{bankDetails?.base_currency?.currency || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-light rounded p-2 me-3">
                                                    <BsInfoCircle className="text-info" size={18} />
                                                </div>
                                                <div>
                                                    <div className="text-muted fs-12 fw-semibold">ACCOUNT NUMBER</div>
                                                    <div className="fw-semibold text-dark text-truncate" style={{maxWidth: '120px'}} title={bankDetails?.account_number}>
                                                        {bankDetails?.account_number || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>

                            {/* Tabs Area */}
                            <div className="p-4">
                                <div className="mb-4">
                                    <div className="nav-tabs-erp border-bottom">
                                        <button
                                            className={`nav-tab-erp ${activeTab === 'transactionList' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('transactionList')}
                                            type="button"
                                        >
                                            <BsArrowLeftRight className="me-2" size={16} />
                                            Transactions
                                        </button>
                                        <button
                                            className={`nav-tab-erp ${activeTab === 'bankDetails' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('bankDetails')}
                                            type="button"
                                        >
                                            <BsInfoCircle className="me-2" size={16} />
                                            Account Details
                                        </button>
                                    </div>
                                </div>

                                <div className="tab-content-erp">
                                    {activeTab === 'transactionList' && (
                                        <div className="bg-white rounded border p-3">
                                            <TransactionList 
                                                bankDetails={bankDetails} 
                                                accountId={bankDetails?.id} 
                                                fetchBankDetails={fetchBankDetails} 
                                                setBankDetails={setBankDetails}
                                                setTemporarilyHidden={setIsTemporarilyHidden}
                                                ledgerEntries={ledgerEntries}
                                                ledgerOpeningBalance={ledgerOpeningBalance}
                                                countries={countries}
                                            />
                                        </div>
                                    )}

                                    {activeTab === 'bankDetails' && (
                                        <div className="bg-white rounded border p-3">
                                            <BankView bankData={bankDetails} setBankDetails={setBankDetails}/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-center align-items-center h-100">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            {showModal && (
                <BankModal
                    showModal={showModal}
                    handleClose={() => {
                        setShowModal(false);
                        setIsTemporarilyHidden(false);
                    }}
                    bankData={selectedBank}
                    editBank={editBank}
                    bankId={bankId}
                    countries={countries}
                    fetchCountries={fetchCountries}
                />
            )}
        </>
    );
};

export default BankDetail;
